import React from 'react';
import { Helmet } from 'react-helmet-async';
import { HeaderNav } from '../components/HeaderNav';
import { Footer } from '../components/Footer';
import { Separator } from '../components/ui/separator';
import { motion } from 'framer-motion';
import { Target, Users, Sparkles, TrendingUp } from 'lucide-react';

export default function AboutPage() {
  const values = [
    {
      icon: Target,
      title: 'Our Mission',
      description: 'To bridge the gap between brands and creators, telling the stories that matter in the evolving creator economy.'
    },
    {
      icon: Users,
      title: 'Our Community',
      description: 'A vibrant ecosystem of brands, bloggers, and industry leaders shaping the future of digital commerce.'
    },
    {
      icon: Sparkles,
      title: 'Our Content',
      description: 'Bold features, honest interviews, and cutting-edge insights into the world of brands and creators.'
    },
    {
      icon: TrendingUp,
      title: 'Our Impact',
      description: 'Empowering the next generation of digital entrepreneurs and innovative brands to tell their stories.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>About Us · B&B Magazine</title>
        <meta name="description" content="Learn about B&B Magazine - where bold voices meet braver brands. Our mission, values, and the story behind Brands & Bloggers Magazine." />
        <link rel="canonical" href="https://cms-production-test.preview.emergentagent.com/about" />
      </Helmet>

      <HeaderNav />

      <main>
        {/* Hero Section */}
        <section className="w-full py-16 md:py-24 relative overflow-hidden">
          <div 
            aria-hidden="true" 
            className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(80%_60%_at_50%_0%,rgba(255,68,102,0.12),rgba(0,0,0,0)_70%)]"
          />
          
          <div className="container max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <span className="uppercase tracking-[0.18em] text-xs text-muted-foreground font-ui">
                About B&B Magazine
              </span>
              <h1 className="mt-3 font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.05]">
                Bold Voices. <br className="sm:hidden" />Braver Brands.
              </h1>
              <p className="mt-6 text-lg md:text-xl text-muted-foreground leading-relaxed max-w-[65ch] mx-auto">
                B&B Magazine is the premier destination for stories at the intersection of brands and creators. 
                We spotlight the entrepreneurs, innovators, and visionaries building the future of digital commerce.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Story Section */}
        <section className="w-full py-12 md:py-16">
          <div className="container max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">Our Story</h2>
              <div className="space-y-4 text-base md:text-[17px] leading-7 text-muted-foreground">
                <p>
                  Founded in the heart of the creator economy boom, B&B Magazine emerged from a simple observation: 
                  the most exciting stories in business were happening at the intersection of personal brands and commercial success.
                </p>
                <p>
                  What started as a passion project has grown into a thriving publication that serves thousands of readers 
                  - from aspiring creators to Fortune 500 marketing teams. We've interviewed industry legends, broken 
                  exclusive stories, and built a community that celebrates authenticity in an increasingly curated digital world.
                </p>
                <p>
                  Today, B&B Magazine stands as the trusted voice covering the evolving landscape where influence meets commerce, 
                  creativity meets strategy, and personal brands become powerful businesses.
                </p>
              </div>
            </motion.div>

            <Separator className="my-12" />

            {/* Values Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-10">What We Stand For</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {values.map((value, index) => (
                  <motion.div
                    key={value.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[hsl(var(--primary)/0.12)] border border-[hsl(var(--primary)/0.3)] flex items-center justify-center group-hover:bg-[hsl(var(--primary)/0.18)] transition-colors duration-200">
                        <value.icon className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-display text-xl font-semibold mb-2">{value.title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {value.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <Separator className="my-12" />

            {/* Team Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-6">Our Editorial Team</h2>
              <p className="text-base md:text-[17px] leading-7 text-muted-foreground max-w-[65ch] mx-auto mb-8">
                B&B Magazine is powered by a diverse team of writers, editors, and industry experts who are passionate 
                about uncovering the stories that matter. From seasoned journalists to creator economy insiders, 
                our team brings decades of combined experience to every piece we publish.
              </p>
              <p className="text-sm text-muted-foreground">
                Interested in contributing? <a href="/contact" className="text-primary hover:underline">Get in touch</a>
              </p>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}